/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Component, Input, Output, EventEmitter, OnInit, APP_INITIALIZER, ViewChild } from "@angular/core";
import { ParameterValue } from "../simple-function-action-editor.component";
import { Event } from "app/models/event";
import { LocationInstance } from "app/models/location-instance";
import { LocationService } from "app/services/location.service";
import { PropertyType } from "app/models/property-type";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component";

@Component({
  selector: 'app-function-parameter-editor-location',
  templateUrl: './location-function-parameter-editor.component.html'
})
export class LocationFunctionParameterEditorComponent implements OnInit {

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  //Event Trigger used for the Rule
  @Input()
  set event(event: Event) {
    this._event = event;
    this.refreshSelectOptions(false, true);
  }
  get event(): Event {
    return this._event;
  }
  _event: Event;

  //Parameter Value being shown / represented
  @Input() 
  parameterValue: ParameterValue;

  //Change Event fired when the value is changed.
  @Output()
  valueChange = new EventEmitter<void>();

  //Flag indicating whether the Select Options are being loaded
  loadingSelectOptions = true;

  //Options available for the Parameter Value
  locationOptions: SelectOption[] = [];
  eventOptions: SelectOption[] = [];
  selectOptions: SelectOption[] = [];


  get selectedOption(): SelectOption {
    if (this.parameterValue != null && this.parameterValue.mValue != null && this.parameterValue.mValue.length > 0) {
      return this.selectOptions.find(option => option.mValue == this.parameterValue.mValue);
    }
    return null;
  }
  set selectedOption(option: SelectOption) {
    if (this.parameterValue != null) {
      this.parameterValue.mValue = option == null? null : option.mValue;
      this.valueChange.emit();
    }
  }



  constructor(
    private mLocationService: LocationService
  ) { }

  ngOnInit(): void {
    this.refreshSelectOptions(true, true);
  }



  async refreshSelectOptions(refreshLocationOptions: boolean, refreshEventOptions: boolean): Promise<void> {
    this.loadingSelectOptions = true;

    if (refreshLocationOptions) {
      await this.refreshLocationOptions();
    }

    if (refreshEventOptions) {
      await this.refreshEventOptions();
    }

    const options: SelectOption[] = [];
    options.push(...this.eventOptions);
    options.push(...this.locationOptions);

    if (this.parameterValue != null && this.parameterValue.mValue != null && this.parameterValue.mValue.length > 0) {
      if (options.find(option => option.mValue == this.parameterValue.mValue) == null) {
        const unknownValueOption = new SelectOption();
        unknownValueOption.mValue = this.parameterValue.mValue;
        unknownValueOption.mDisplay = "UNKNOWN VALUE => " + this.parameterValue.mValue;
        options.push(unknownValueOption);
      }
    }
    
    this.selectOptions = options;

    this.loadingSelectOptions = false;
  }

  private async refreshLocationOptions(): Promise<void> {
    let locations: LocationInstance[];
    try {
      locations = await this.mLocationService.getAllLocationInstances();
    } catch (error) {
      this.handleError(error);
    }

    if (locations == null) {
      this.locationOptions = [];
    } else {
      const options: SelectOption[] = [];
      for (const location of locations) {
        const option = new SelectOption();
        option.mValue = location.id.toString();
        option.mDisplay = location.name;
        options.push(option);
      }
      this.locationOptions = options;
    }
  }

  private async refreshEventOptions(): Promise<void> {
    const options: SelectOption[] = [];
    if (this.event != null) {
      
      const eventLocationOption = new SelectOption();
      eventLocationOption.mValue = "${event._location}";
      eventLocationOption.mDisplay = "FROM EVENT => Event Location";
      options.push(eventLocationOption);

      if (this.event.properties != null) {
        for (const eventProperty of this.event.properties) {
          if (eventProperty.type == PropertyType.LOCATION_REFERENCE) {
            const option = new SelectOption();
            option.mValue = "${event." + eventProperty.property + "}";
            option.mDisplay = "FROM EVENT => " + eventProperty.name;
            options.push(option);
          }
        }
      }
    }

    this.eventOptions = options;
  }

  handleError(error: any): void {
    console.error("handleError() - Error: " + error);
    if (error != null) {
      const response: Response = error;
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, response.text().toString());
    }
  }

}

class SelectOption {
  mValue: string;
  mDisplay: string;
}
